<template>
  <v-container fluid class="pt-0">
    <v-card v-if="loading" class="text-center pa-5">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-4">Loading user data...</p>
    </v-card>

    <v-alert v-if="error" type="error" prominent>
      Could not load user data. Please try again later.
    </v-alert>

    <v-card v-if="user && !loading && !error" :class="{ 'editing-background': isEditing }">
      <v-card-title class="d-flex align-center">
        <v-avatar color="primary" class="mr-3">
          <v-img v-if="user.picture" :src="user.picture" id="user-picture" alt="User Avatar"></v-img>
          <span v-else-if="user.name">{{ user.name.charAt(0) }}</span>
          <v-icon v-else>fas fa-user</v-icon>
        </v-avatar>
        <template v-if="isEditing && user.user_id && !user.user_id.startsWith('google-oauth2')">
          <v-text-field v-model="editableUser.name" label="Name" id="user-name" hide-details dense></v-text-field>
        </template>
        <template v-else>
          <span>{{ user.name }}</span>
        </template>
        <div class="d-flex align-center ga-2 action-buttons">
          <v-chip :color="user.account_status === 'Active' ? 'green' : user.account_status === 'Pending' ? 'orange' : 'red'" dark>{{ user.account_status }}</v-chip>
          <v-btn icon @click="handleMarkAsAdmin" v-if="isEditing && user && user.email && user.email.endsWith('@structure.io')" data-testid="mark-admin-button">
            <v-icon>fas fa-user-shield</v-icon>
          </v-btn>
          <v-btn icon @click="showEnableTrialModal = true" v-if="!isEditing && !user.is_trial" data-testid="enable-trial-button">
            <v-icon>fas fa-clock</v-icon>
          </v-btn>
          <v-btn icon @click="showExpireTrialModal = true" v-if="!isEditing && user.is_trial" data-testid="expire-trial-button">
            <v-icon>fas fa-hourglass-end</v-icon>
          </v-btn>
          <v-btn icon @click="showBillingAddressModal = true" v-if="!isEditing" data-testid="billing-address-button" :title="user.has_billing_info ? 'Update Billing Address' : 'Add Billing Address'">
            <v-icon>fas fa-file-invoice-dollar</v-icon>
          </v-btn>
          <v-btn icon @click="startEditing" v-if="!isEditing" data-testid="edit-button">
            <v-icon data-testid="edit-icon">fas fa-edit</v-icon>
          </v-btn>
          <v-btn icon @click="closeView">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-divider></v-divider>

      <v-list dense>
        <v-list-item>
          <v-list-item-title>Email</v-list-item-title>
          <template v-if="isEditing && user.user_id && !user.user_id.startsWith('google-oauth2')">
            <v-text-field v-model="editableUser.email" id="user-email" label="Email" hide-details dense></v-text-field>
          </template>
          <template v-else>
            <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
          </template>
        </v-list-item>

        <v-list-item>
          <v-list-item-title>Company</v-list-item-title>
          <template v-if="isEditing">
            <v-text-field v-model="editableUser.organization" id="user-organization" label="Company" hide-details dense></v-text-field>
          </template>
          <template v-else>
            <v-list-item-subtitle>{{ user.organization || 'N/A' }}</v-list-item-subtitle>
          </template>
        </v-list-item>

        <v-list-item>
          <v-list-item-title>Account Status</v-list-item-title>
          <template v-if="isEditing">
            <v-select
              v-model="editableUser.account_status"
              :items="['Active', 'Pending', 'Suspended']"
              label="Account Status"
              hide-details
              dense
            ></v-select>
          </template>
          <template v-else>
            <v-list-item-subtitle>
              <v-chip :color="user.account_status === 'Active' ? 'green' : user.account_status === 'Pending' ? 'orange' : 'red'" dark>{{ user.account_status }}</v-chip>
            </v-list-item-subtitle>
          </template>
        </v-list-item>

        <v-list-item>
          <v-list-item-title>Subscription Level</v-list-item-title>
          <template v-if="isEditing">
            <v-select
              v-model="editableUser.tier_id"
              :items="allTiers"
              :item-props="tierItemProps"
              item-title="name"
              item-value="id"
              label="Subscription Level"
              hide-details
              dense
            ></v-select>
          </template>
          <template v-else>
            <v-list-item-subtitle><v-chip>{{ user.tier || 'N/A' }}</v-chip></v-list-item-subtitle>
          </template>
        </v-list-item>

        <v-list-item>
          <v-list-item-title>Trial Access</v-list-item-title>
          <v-list-item-subtitle>
            <v-chip :color="user.is_trial ? 'blue' : 'grey'" dark>{{ user.is_trial ? 'Yes' : 'No' }}</v-chip>
            <span v-if="user.is_trial && user.trial_expires_at" class="ml-2">
              (Expires: {{ formattedTrialExpiresAt }})
            </span>
          </v-list-item-subtitle>
        </v-list-item>

        <v-list-item>
          <v-list-item-title>Terms Version</v-list-item-title>
          <v-list-item-subtitle>{{ user.terms_version }}</v-list-item-subtitle>
        </v-list-item>

        <v-list-item>
          <v-list-item-title>Last Login</v-list-item-title>
          <v-list-item-subtitle>{{ formattedLastLogin }}</v-list-item-subtitle>
        </v-list-item>

        <v-list-item>
          <v-list-item-title>Latest SDK Version Downloaded</v-list-item-title>
          <v-list-item-subtitle>{{ user.latest_sdk_download || 'None' }}</v-list-item-subtitle>
        </v-list-item>

        <v-list-item>
          <v-list-item-title>Internal Notes</v-list-item-title>
          <template v-if="isEditing">
            <v-textarea
              v-model="editableUser.internal_notes"
              id="user-internal-notes"
              label="Internal Notes"
              hide-details
              dense
              rows="3"
            ></v-textarea>
          </template>
          <template v-else>
            <v-list-item-subtitle>{{ user.internal_notes || 'None' }}</v-list-item-subtitle>
          </template>
        </v-list-item>


        <v-list-item>
          <v-list-item-title>Billing Address</v-list-item-title>
          <v-list-item-subtitle v-if="user.billing_address">
            <div>{{ user.billing_address.street_1 }}</div>
            <div v-if="user.billing_address.street_2">{{ user.billing_address.street_2 }}</div>
            <div>{{ user.billing_address.city }}, {{ user.billing_address.state }} {{ user.billing_address.postal_code }}</div>
            <div>{{ user.billing_address.country }}</div>
            <div v-if="user.billing_address.billing_email">{{ user.billing_address.billing_email }}</div>
            <div v-if="user.billing_address.vat_number">VAT: {{ user.billing_address.vat_number }}</div>
          </v-list-item-subtitle>
          <v-list-item-subtitle v-else>None</v-list-item-subtitle>
        </v-list-item>

        <v-list-item v-if="user.roles && user.roles.length">
          <v-list-item-title>Roles</v-list-item-title>
          <v-list-item-subtitle>
            <div class="d-flex flex-wrap align-center">
              <v-chip v-for="role in user.roles" :key="role.id" small class="ma-1">{{ role.name }}</v-chip>
              <v-btn v-if="isEditing" icon size="small" @click="showAddRoleModal = true">
                <v-icon>fas fa-plus-circle</v-icon>
              </v-btn>
            </div>
          </v-list-item-subtitle>
        </v-list-item>



        <v-list-item v-if="user.permissions && user.permissions.length">
          <v-list-item-title>Special File Permissions</v-list-item-title>
          <v-list-item-subtitle>
            <div class="d-flex flex-wrap align-center">
              <v-chip v-for="permission in user.permissions" :key="permission.permission_name" small class="ma-1" data-testid="permission-chip">{{ permission.permission_name }}</v-chip>
              <v-btn v-if="isEditing" icon size="small" @click="showAddPermissionModal = true">
                <v-icon>fas fa-plus-circle</v-icon>
              </v-btn>
            </div>
          </v-list-item-subtitle>
        </v-list-item>
      </v-list>

      <v-card-actions v-if="showBuildEnterpriseQuoteButton">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          id="build-enterprise-quote-btn"
          @click="navigateToBuildQuote"
        >
          Build Enterprise Quote
        </v-btn>
      </v-card-actions>

      <v-card-actions v-if="isEditing">
        <v-spacer></v-spacer>
        <v-btn color="red" id="cancel-btn" text @click="cancelEditing">Cancel</v-btn>
        <v-btn color="primary" id="submit-btn" text @click="confirmAndUpdate">Update User</v-btn>
      </v-card-actions>
    </v-card>

    <AddRolePermissionModal
      v-if="isEditing && editableUser && editableUser.roles"
      :model-value="showAddRoleModal"
      title="Add/Remove Roles"
      :available-items="allRoles"
      :initial-selected-items="editableUser.roles"
      item-title-key="name"
      item-value-key="id"
      select-label="Select Roles"
      @update:model-value="showAddRoleModal = $event"
      @save="updateRoles"
    />



    <AddRolePermissionModal
      v-if="isEditing && editableUser && editableUser.permissions"
      :model-value="showAddPermissionModal"
      title="Add/Remove Permissions"
      :available-items="allPermissions"
      :initial-selected-items="editableUser.permissions"
      item-title-key="value"
      item-value-key="id"
      select-label="Select Permissions"
      @update:model-value="showAddPermissionModal = $event"
      @save="updatePermissions"
    />

    <ChangesConfirmationModal
      :model-value="showUpdateConfirmModal"
      title="Confirm User Update"
      :changes="proposedChanges"
      @update:model-value="showUpdateConfirmModal = $event"
      @confirm="handleUpdateConfirm"
      @cancel="handleUpdateCancel"
    />

    <ConfirmationModal
      :model-value="showMarkAsAdminModal"
      title="Mark User as Admin"
      message="Are you sure? Marking this user as admin will give them sensitive privileges."
      confirm-text="I'm Sure"
      @update:model-value="showMarkAsAdminModal = $event"
      @confirm="confirmMarkAsAdmin"
      @cancel="cancelMarkAsAdmin"
    />

    <EnableTrialModal
      :model-value="showEnableTrialModal"
      @update:model-value="showEnableTrialModal = $event"
      @start="handleStartTrial"
      @cancel="showEnableTrialModal = false"
    />

    <ExpireTrialModal
      :model-value="showExpireTrialModal"
      @update:model-value="showExpireTrialModal = $event"
      @confirm="handleExpireTrial"
      @cancel="showExpireTrialModal = false"
    />

    <BillingAddressModal
      :model-value="showBillingAddressModal"
      :initial-address="user ? user.billing_address : null"
      @update:model-value="showBillingAddressModal = $event"
      @save="handleBillingAddressSave"
      @cancel="showBillingAddressModal = false"
    />

    <v-snackbar v-model="showSuccessTooltip" :timeout="3000" color="success">
      User updated successfully.
    </v-snackbar>

  </v-container>
</template>

<script>
import {ref, onMounted, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-vue';
import { useStore } from 'vuex';
import AddRolePermissionModal from '@/modals/AddRolePermissionModal.vue';
import BillingAddressModal from '@/modals/BillingAddressModal.vue';
import ChangesConfirmationModal from '@/modals/ChangesConfirmationModal.vue';
import ConfirmationModal from '@/modals/ConfirmationModal.vue';
import EnableTrialModal from '@/modals/EnableTrialModal.vue';
import ExpireTrialModal from '@/modals/ExpireTrialModal.vue';

export default {
  name: 'IndividualUser',
  components: {
    AddRolePermissionModal,
    BillingAddressModal,
    ChangesConfirmationModal,
    ConfirmationModal,
    EnableTrialModal,
    ExpireTrialModal,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter(); // Get router instance
    const { getAccessTokenSilently } = useAuth0(); // Renamed user to auth0User to avoid conflict
    const user = ref(null);
    const loading = ref(true);
    const error = ref(false);
    const isEditing = ref(false);
    const editableUser = ref({});
    const showAddRoleModal = ref(false);
    const showAddPermissionModal = ref(false);
    const showUpdateConfirmModal = ref(false);
    const showSuccessTooltip = ref(false);
    const showMarkAsAdminModal = ref(false);
    const showEnableTrialModal = ref(false);
    const showExpireTrialModal = ref(false);
    const showBillingAddressModal = ref(false);

    const allRoles = ref([]);
    const allPermissions = ref([]);

    const allTiers = ref([]);

    const proposedChanges = computed(() => {
      if (!user.value || !editableUser.value) return [];
      const changes = [];
      const original = user.value;
      const edited = editableUser.value;

      if (original.user_id && !original.user_id.startsWith('google-oauth2')) {
        if (original.name !== edited.name) {
          changes.push(`Name: "${original.name}" -> "${edited.name}"`);
        }
        if (original.email !== edited.email) {
          changes.push(`Email: "${original.email}" -> "${edited.email}"`);
        }
      }

      if ((original.organization || 'N/A') !== (edited.organization || 'N/A')) {
        changes.push(`Company: "${original.organization || 'N/A'}" -> "${edited.organization || 'N/A'}"`);
      }
      if (original.account_status !== edited.account_status) {
        changes.push(`Account Status: "${original.account_status}" -> "${edited.account_status}"`);
      }
      if ((original.internal_notes || 'None') !== (edited.internal_notes || 'None')) {
        changes.push(`Internal Notes: "${original.internal_notes || 'None'}" -> "${edited.internal_notes || 'None'}"`);
      }

      const originalTier = allTiers.value.find(t => t.name === original.tier);
      const originalTierId = originalTier ? originalTier.id : null;
      const newTierId = edited.tier_id;

      if (originalTierId !== newTierId) {
          const originalTierName = original.tier || 'N/A';
          const newTier = allTiers.value.find(t => t.id === newTierId);
          const newTierName = newTier ? newTier.name : 'N/A';
          changes.push(`Subscription Level: "${originalTierName}" -> "${newTierName}"`);
      }

      const originalRoleIds = new Set((original.roles || []).map(r => r.id));
      const editedRoleIds = new Set((edited.roles || []).map(r => r.id));

      if (JSON.stringify([...originalRoleIds].sort()) !== JSON.stringify([...editedRoleIds].sort())) {
          const originalRoleNames = new Set((original.roles || []).map(r => r.name));
          const editedRoleNames = new Set((edited.roles || []).map(r => r.name));
          const added = [...editedRoleNames].filter(name => !originalRoleNames.has(name));
          const removed = [...originalRoleNames].filter(name => !editedRoleNames.has(name));

          if (added.length > 0) changes.push(`Roles added: ${added.join(', ')}`);
          if (removed.length > 0) changes.push(`Roles removed: ${removed.join(', ')}`);
      }

      const originalPermNames = new Set((original.permissions || []).map(p => p.permission_name));
      const editedPermIds = new Set((edited.permissions || []).map(p => p.id));

      if (JSON.stringify([...originalPermNames].sort()) !== JSON.stringify([...editedPermIds].sort())) {
          const added = [...editedPermIds].filter(id => !originalPermNames.has(id));
          const removed = [...originalPermNames].filter(id => !editedPermIds.has(id));

          if (added.length > 0) changes.push(`Permissions added: ${added.join(', ')}`);
          if (removed.length > 0) changes.push(`Permissions removed: ${removed.join(', ')}`);
      }

      return changes;
    });

    const confirmAndUpdate = () => {
      if (proposedChanges.value.length === 0) {
        return;
      }
      showUpdateConfirmModal.value = true;
    };

    const handleUpdateConfirm = async () => {
      showUpdateConfirmModal.value = false;
      await saveChanges();
    };

    const handleUpdateCancel = () => {
      showUpdateConfirmModal.value = false;
    };

    const startEditing = () => {
      // Make a deep copy of the user object
      editableUser.value = JSON.parse(JSON.stringify(user.value));

      // Ensure roles and permissions exist as arrays
      if (!editableUser.value.roles) {
          editableUser.value.roles = [];
      }
      if (!editableUser.value.permissions) {
          editableUser.value.permissions = [];
      }

      // Create a Set of existing role IDs to efficiently map to full role objects
      const existingRoleIds = new Set(editableUser.value.roles.map(r => r.id));
      editableUser.value.roles = allRoles.value.filter(r => existingRoleIds.has(r.id));

      // Create a Set of existing permission names to map to full permission objects
      const existingPermissionNames = new Set(editableUser.value.permissions.map(p => p.permission_name));
      editableUser.value.permissions = allPermissions.value.filter(p => existingPermissionNames.has(p.id));

      const currentTier = allTiers.value.find(t => t.name === user.value.tier);
      editableUser.value.tier_id = currentTier ? currentTier.id : null;

      isEditing.value = true;
    };

    const cancelEditing = () => {
      isEditing.value = false;
      editableUser.value = null; // Clear editable user data
    };

    const saveChanges = async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };

        const payload = {};
        const original = user.value;
        const edited = editableUser.value;

        if (original.user_id && !original.user_id.startsWith('google-oauth2')) {
          if (original.name !== edited.name) payload.name = edited.name;
          if (original.email !== edited.email) payload.email = edited.email;
        }

        if (original.organization !== edited.organization) payload.organization = edited.organization;
        if (original.account_status !== edited.account_status) payload.account_status = edited.account_status;
        if ((original.internal_notes || '') !== (edited.internal_notes || '')) payload.internal_notes = edited.internal_notes;

        const originalTier = allTiers.value.find(t => t.name === original.tier);
        const originalTierId = originalTier ? originalTier.id : null;
        if (originalTierId !== edited.tier_id) {
          payload.tier_id = edited.tier_id;
        }

        const originalRoleIds = new Set((original.roles || []).map(r => r.id));
        const editedRoleIds = new Set((edited.roles || []).map(r => r.id));
        if (JSON.stringify([...originalRoleIds].sort()) !== JSON.stringify([...editedRoleIds].sort())) {
            payload.roles = edited.roles.map(r => r.id);
        }

        const originalPermNames = new Set((original.permissions || []).map(p => p.permission_name));
        const editedPermIds = new Set((edited.permissions || []).map(p => p.id));
        if (JSON.stringify([...originalPermNames].sort()) !== JSON.stringify([...editedPermIds].sort())) {
            payload.permissions = edited.permissions.map(p => p.id);
        }

        if (Object.keys(payload).length === 0) {
            isEditing.value = false;
            editableUser.value = null;
            return;
        }

        await axios.patch(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/users/${route.params.id}`, payload, { headers });

        await fetchUser();
        isEditing.value = false;
        editableUser.value = null;
        showSuccessTooltip.value = true;

      } catch (e) {
        store.commit('SET_ERROR_MESSAGE', e.response?.data?.message || e.message || 'An unknown error occurred.');
        store.commit('SET_SHOW_ERROR_MODAL', true);
      }
    };

    const updateRoles = (newRoles) => {
      if (editableUser.value) {
        editableUser.value.roles = newRoles; // newRoles is an array of role objects
      }
    };

    const updatePermissions = (newPermissions) => {
      if (editableUser.value) {
        editableUser.value.permissions = newPermissions; // newPermissions is an array of permission objects
      }
    };

    const fetchUser = async () => {
      loading.value = true;
      error.value = false;
      try {
        const token = await getAccessTokenSilently();
        const userId = route.params.id;
        const headers = { Authorization: `Bearer ${token}` }; // Define headers here

        const [userResponse, rolesResponse, permissionsResponse, tiersResponse] = await Promise.all([
          axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/users/${userId}`, { headers }),
          axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/roles`, { headers }), // Fetch all roles
          axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/permissions`, { headers }), // Fetch all permissions
          axios.get(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/tiers`, { headers }), // Fetch all tiers
        ]);

        user.value = userResponse.data;
        allRoles.value = rolesResponse.data; // Populate allRoles
        allPermissions.value = permissionsResponse.data; // Populate allPermissions
        allTiers.value = tiersResponse.data; // Populate allTiers

      } catch (e) {
        error.value = true;
      } finally {
        loading.value = false;
      }
    };



    const formattedLastLogin = computed(() => {
      if (user.value && user.value.last_login) {
        return new Date(user.value.last_login).toLocaleString();
      }
      return 'Never';
    });

    const formattedTrialExpiresAt = computed(() => {
      if (user.value && user.value.trial_expires_at) {
        return new Date(user.value.trial_expires_at).toLocaleString();
      }
      return 'N/A';
    });



    const closeView = () => {
      router.push({ name: 'AllUsers' });
    };

    const tierItemProps = (item) => {
      return {
        disabled: ['Basic', 'Professional', 'Premium'].includes(item.name)
      }
    };

    onMounted(async () => {
      await fetchUser();
    });

    watch(
      () => route.params.id,
      (newId, oldId) => {
        if (newId !== oldId) {
          fetchUser();
        }
      }
    );

    const handleMarkAsAdmin = () => {
      showMarkAsAdminModal.value = true;
    };

    const confirmMarkAsAdmin = async () => {
      showMarkAsAdminModal.value = false;
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };

        await axios.post(`${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/users/${route.params.id}/make-admin`, null, { headers });

        showSuccessTooltip.value = true;
        await fetchUser();

      } catch (e) {
        store.commit('SET_ERROR_MESSAGE', e.response?.data?.message || e.message || 'An unknown error occurred while making the user an admin.');
        store.commit('SET_SHOW_ERROR_MODAL', true);
      }
    };

    const cancelMarkAsAdmin = () => {
      showMarkAsAdminModal.value = false;
    };

    const handleExpireTrial = async () => {
      showExpireTrialModal.value = false;
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };

        await axios.post(
          `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/users/${route.params.id}/expire-trial`,
          null,
          { headers }
        );

        showSuccessTooltip.value = true;
        await fetchUser();
      } catch (e) {
        store.commit('SET_ERROR_MESSAGE', e.response?.data?.message || e.message || 'An unknown error occurred while expiring the trial.');
        store.commit('SET_SHOW_ERROR_MODAL', true);
      }
    };

    const handleStartTrial = async (trialExpiresAt) => {
      showEnableTrialModal.value = false;
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };

        await axios.patch(
          `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/users/${route.params.id}`,
          { is_trial: true, trial_expires_at: trialExpiresAt },
          { headers }
        );

        showSuccessTooltip.value = true;
        await fetchUser();
      } catch (e) {
        store.commit('SET_ERROR_MESSAGE', e.response?.data?.message || e.message || 'An unknown error occurred while enabling trial.');
        store.commit('SET_SHOW_ERROR_MODAL', true);
      }
    };

    const handleBillingAddressSave = async (addressData) => {
      showBillingAddressModal.value = false;
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };

        const payload = Object.fromEntries(
          Object.entries(addressData).filter(([, v]) => v !== '' && v !== null && v !== undefined)
        );

        await axios.put(
          `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/users/${route.params.id}/billing-address`,
          payload,
          { headers }
        );

        showSuccessTooltip.value = true;
        await fetchUser();
      } catch (e) {
        store.commit('SET_ERROR_MESSAGE', e.response?.data?.message || e.message || 'An unknown error occurred while saving the billing address.');
        store.commit('SET_SHOW_ERROR_MODAL', true);
      }
    };

    const showBuildEnterpriseQuoteButton = computed(() => {
        return user.value &&
               user.value.tier === 'Enterprise' &&
               user.value.has_billing_info &&
               !user.value.has_quote;
    });

    /* eslint-disable no-unused-vars */
    const navigateToBuildQuote = () => {
        router.push({ name: 'QuoteGenerationView', params: { id: route.params.id } });
    };

    return {
      user,
      loading,
      error,
      formattedLastLogin,
      formattedTrialExpiresAt,
      closeView,
      isEditing,
      editableUser,
      startEditing,
      cancelEditing,
      saveChanges,
      showAddRoleModal,
      showAddPermissionModal,
      allRoles,
      allPermissions,
      updateRoles,
      updatePermissions,
      showUpdateConfirmModal,
      showSuccessTooltip,
      proposedChanges,
      confirmAndUpdate,
      handleUpdateConfirm,
      handleUpdateCancel,
      allTiers,
      tierItemProps,
      showMarkAsAdminModal,
      handleMarkAsAdmin,
      confirmMarkAsAdmin,
      cancelMarkAsAdmin,
      showEnableTrialModal,
      handleStartTrial,
      showExpireTrialModal,
      handleExpireTrial,
      showBillingAddressModal,
      handleBillingAddressSave,
      showBuildEnterpriseQuoteButton,
      navigateToBuildQuote
    };
  },
};
</script>

<style scoped>
.editing-background {
  background-color: #333333;
}

.action-buttons {
  overflow-x: auto;
  flex: 1 1 auto;
  min-width: 0;
  margin-left: auto;
  scrollbar-width: thin;
}

.action-buttons::-webkit-scrollbar {
  height: 4px;
}

.action-buttons::-webkit-scrollbar-track {
  background: transparent;
}

.action-buttons::-webkit-scrollbar-thumb {
  background-color: rgba(128, 128, 128, 0.4);
  border-radius: 2px;
}
</style>